import { distinctUntilChanged, filter, fromEvent, map, merge, Observable, Subject, Subscription } from '@tanbo/stream'
import { Injectable } from '@viewfly/core'
import {
  Commander,
  CompositionStartEventData,
  ContentType,
  Controller,
  Event,
  invokeListener,
  Keyboard,
  Scheduler,
  Selection,
  Slot,
  Textbus
} from '@textbus/core'

import {
  CaretPresentation,
  computeCaretPresentation,
  createElement,
  getLayoutRectByRange,
  getOverflowClipContext,
  measureCaretBoxLayout,
  measureInlineCaretRotate,
  OverflowClipContext,
  Rect,
} from './_utils/uikit'
import { Parser } from './parser'
import { isFirefox, isMac, isSafari, isWindows } from './_utils/env'
import { VIEW_MASK } from './injection-tokens'
import { Caret, CaretLimit, CaretPosition, caretPositionEqual, Input } from './types'
import { DomAdapter } from './dom-adapter'

const iframeHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="ie=edge">
  <title>Textbus</title>
  <style>
    html {position: fixed; left:0; overflow: hidden}
    html, body{height: 100%;width:100%}
    body{margin:0; overflow: hidden}
    textarea{width: 2000px;height: 100%;opacity: 0; padding: 0; outline: none; border: none; position: absolute; left:0; top:0;}
  </style>
</head>
<body>
</body>
</html>
`

interface CaretStyle {
  height: string
  lineHeight: string
  fontSize: string
}

class ExperimentalCaret implements Caret {
  onPositionChange: Observable<CaretPosition | null>
  onStyleChange: Observable<CaretStyle>
  elementRef: HTMLElement
  changeFromSelf = false

  getLimit = function (): CaretLimit {
    return {
      top: 0,
      bottom: document.documentElement.clientHeight
    }
  }

  get rect() {
    return this.caret.getBoundingClientRect()
  }

  private timer: any = null
  private caret: HTMLElement

  private set display(v: boolean) {
    this._display = v
    if (!this.caretClippedOut) {
      this.caret.style.visibility = v ? 'visible' : 'hidden'
    }
  }

  private get display() {
    return this._display
  }

  private _display = true
  private flashing = true
  /** 被 overflow 裁出可视区（不用 display:none，避免触发 Input.hide） */
  private caretClippedOut = false

  private subscription = new Subscription()
  private scrollSubscription = new Subscription()
  private scrollRaf = 0
  private clipContext: OverflowClipContext | null = null
  private clipContextAnchor: Node | null = null
  private scrollListenerAnchor: Node | null = null

  private positionChangeEvent = new Subject<CaretPosition | null>()
  private styleChangeEvent = new Subject<CaretStyle>()
  private oldRange: Range | null = null

  constructor(
    private domRenderer: DomAdapter,
    private scheduler: Scheduler,
    private editorMask: HTMLElement) {
    this.onPositionChange = this.positionChangeEvent.pipe(distinctUntilChanged(caretPositionEqual))
    this.onStyleChange = this.styleChangeEvent.asObservable()
    this.elementRef = createElement('div', {
      styles: {
        position: 'absolute',
        width: '2px',
        pointerEvents: 'none'
      },
      children: [
        this.caret = createElement('span', {
          styles: {
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: 0,
            top: 0
          }
        })
      ]
    })

    this.subscription.add(
      fromEvent(document, 'mousedown').subscribe(() => {
        this.flashing = false
      }),
      fromEvent(document, 'mouseup').subscribe(() => {
        this.flashing = true
      }),
    )
    this.editorMask.appendChild(this.elementRef)
  }

  refresh() {
    if (this.oldRange?.collapsed) {
      this.updateCursorPosition(this.oldRange)
    }
  }

  show(range: Range, restart: boolean) {
    this.oldRange = range
    if (restart || this.scheduler.lastChangesHasLocalUpdate) {
      clearTimeout(this.timer)
    }
    if (range.collapsed && (restart || this.scheduler.lastChangesHasLocalUpdate)) {
      this._display = true
    }
    this.updateCursorPosition(range)
    if (range.collapsed) {
      const anchor = range.startContainer.nodeType === Node.ELEMENT_NODE ?
        range.startContainer :
        range.startContainer.parentNode
      if (anchor) {
        this.bindScrollListeners(anchor)
      }
      if (restart || this.scheduler.lastChangesHasLocalUpdate) {
        this.display = true
        const toggleShowHide = () => {
          this.display = !this.display || !this.flashing
          this.timer = setTimeout(toggleShowHide, 400)
        }
        clearTimeout(this.timer)
        this.timer = setTimeout(toggleShowHide, 400)
      }
    } else {
      this.unbindScrollListeners()
      this.display = false
      clearTimeout(this.timer)
    }
  }

  hide() {
    this.unbindScrollListeners()
    this.clearClipContext()
    this.display = false
    clearTimeout(this.timer)
    this.caretClippedOut = false
    this.elementRef.style.clipPath = 'none'
    this.elementRef.style.visibility = ''
    this.caret.style.visibility = 'hidden'
    this.positionChangeEvent.next(null)
  }

  destroy() {
    clearTimeout(this.timer)
    this.unbindScrollListeners()
    this.subscription.unsubscribe()
  }

  private bindScrollListeners(anchor: Node) {
    if (this.scrollListenerAnchor === anchor) {
      return
    }
    this.unbindScrollListeners()
    this.scrollListenerAnchor = anchor
    const onScroll = () => {
      if (!this.oldRange || this.scrollRaf) {
        return
      }
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = 0
        if (this.oldRange) {
          this.updateCursorPosition(this.oldRange)
        }
      })
    }
    for (const el of this.ensureClipContext(anchor).scrollContainers) {
      this.scrollSubscription.add(fromEvent(el, 'scroll').subscribe(onScroll))
    }
    const win = anchor.ownerDocument?.defaultView
    if (win) {
      this.scrollSubscription.add(fromEvent(win, 'scroll').subscribe(onScroll))
    }
  }

  private unbindScrollListeners() {
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf)
      this.scrollRaf = 0
    }
    this.scrollSubscription.unsubscribe()
    this.scrollSubscription = new Subscription()
    this.scrollListenerAnchor = null
  }

  private ensureClipContext(anchor: Node): OverflowClipContext {
    if (this.clipContextAnchor !== anchor) {
      this.clipContextAnchor = anchor
      this.clipContext = getOverflowClipContext(anchor, this.editorMask)
    }
    return this.clipContext!
  }

  private clearClipContext() {
    this.clipContext = null
    this.clipContextAnchor = null
  }

  private updateCursorPosition(nativeRange: Range) {
    const node = this.resolveCaretAnchor(nativeRange)
    if (!node) {
      this.positionChangeEvent.next(null)
      return
    }

    nativeRange = this.normalizeCollapsedRange(nativeRange)
    this.caret.style.display = nativeRange.collapsed ? 'block' : 'none'
    if (!nativeRange.collapsed) {
      return
    }

    const contentRect = getLayoutRectByRange(nativeRange)
    const nodeStyle = getComputedStyle(node)
    const {boxHeight, rectTop} = measureCaretBoxLayout(contentRect, nodeStyle)
    const maskRect = this.editorMask.getBoundingClientRect()
    const clipContext = this.ensureClipContext(node)
    const maskLeft = Math.floor(contentRect.left + contentRect.width / 2 - maskRect.left)
    const maskTop = Math.floor(rectTop - maskRect.top)
    const initialRotate = Math.round(Math.atan2(contentRect.width, contentRect.height) * 180 / Math.PI)
    const rotate = measureInlineCaretRotate(node, initialRotate, nodeStyle.writingMode)

    this.applyCaretElementLayout(maskLeft, maskTop, boxHeight, nodeStyle.fontSize, rotate)

    const presentation = computeCaretPresentation({
      anchor: node,
      maskRect,
      clipAncestors: clipContext.clipAncestors,
      maskLeft,
      maskTop,
      boxHeight,
      rectTop,
      contentRect,
      fontSize: nodeStyle.fontSize,
      color: nodeStyle.color,
      measureElementRect: () => this.elementRef.getBoundingClientRect(),
    })

    this.applyCaretPresentation(presentation)
    this.emitCaretPresentation(presentation)
    this.scrollCaretIntoViewIfNeeded(presentation.layoutElRect, clipContext)
  }

  private resolveCaretAnchor(nativeRange: Range): HTMLElement | null {
    const startContainer = nativeRange.startContainer
    const node = (startContainer.nodeType === Node.ELEMENT_NODE ?
      startContainer :
      startContainer.parentNode) as HTMLElement
    return node?.nodeType === Node.ELEMENT_NODE ? node : null
  }

  private normalizeCollapsedRange(nativeRange: Range): Range {
    const compositionNode = this.domRenderer.compositionNode
    if (!compositionNode) {
      return nativeRange
    }
    const range = nativeRange.cloneRange()
    range.selectNodeContents(compositionNode)
    range.collapse()
    return range
  }

  private applyCaretElementLayout(
    maskLeft: number,
    maskTop: number,
    boxHeight: number,
    fontSize: string,
    rotate: number,
  ) {
    Object.assign(this.elementRef.style, {
      left: maskLeft + 'px',
      top: maskTop + 'px',
      height: boxHeight + 'px',
      lineHeight: boxHeight + 'px',
      fontSize,
      transform: `rotate(${rotate}deg)`,
    })
  }

  private applyCaretPresentation(presentation: CaretPresentation) {
    this.caretClippedOut = presentation.outOfView
    if (presentation.outOfView) {
      this.elementRef.style.clipPath = 'none'
      this.elementRef.style.visibility = 'hidden'
      this.caret.style.visibility = 'hidden'
    } else {
      this.elementRef.style.clipPath = presentation.clipPath
      this.elementRef.style.visibility = ''
      this.caret.style.visibility = this._display ? 'visible' : 'hidden'
    }
    this.caret.style.backgroundColor = presentation.caretColor
  }

  private emitCaretPresentation(presentation: CaretPresentation) {
    this.styleChangeEvent.next(presentation.style)
    this.positionChangeEvent.next(presentation.position)
  }

  private scrollCaretIntoViewIfNeeded(layoutElRect: Rect, clipContext: OverflowClipContext) {
    if (!this.changeFromSelf) {
      return
    }
    this.changeFromSelf = false
    const limit = this.getLimit()

    // 从外到内逐层处理，每滚动一层后重新获取光标位置再处理内层
    for (const scrollContainer of [...clipContext.scrollContainers].reverse()) {
      const doc = scrollContainer.ownerDocument ?? document
      const isDoc = scrollContainer === doc.documentElement
      const scrollRect = isDoc
        ? {top: 0, bottom: doc.documentElement.clientHeight}
        : scrollContainer.getBoundingClientRect()

      const visibleTop = Math.max(limit.top, scrollRect.top)
      const visibleBottom = Math.min(limit.bottom, scrollRect.bottom)
      const caretBottom = layoutElRect.top + layoutElRect.height

      if (layoutElRect.top >= visibleTop && caretBottom <= visibleBottom) {
        continue // 光标已在当前容器可视区域内，无需滚动
      }

      if (layoutElRect.top < visibleTop) {
        scrollContainer.scrollTop -= visibleTop - layoutElRect.top
      } else if (caretBottom > visibleBottom) {
        scrollContainer.scrollTop += caretBottom - visibleBottom
      }

      layoutElRect = this.elementRef.getBoundingClientRect()
    }
  }
}


/**
 * Textbus PC 端输入实现
 */
@Injectable()
export class MagicInput extends Input {
  composition = false
  onReady: Promise<void>
  caret: ExperimentalCaret

  set disabled(b: boolean) {
    this._disabled = b
    if (b && this.textarea) {
      this.textarea.disabled = b
    }
  }

  get disabled() {
    return this._disabled
  }

  private isSafari = isSafari()
  private isFirefox = isFirefox()
  private isMac = isMac()
  private isWindows = isWindows()
  private _disabled = false
  private container = this.createEditableFrame()

  private subscription = new Subscription()
  private doc!: Document

  private textarea: HTMLTextAreaElement | null = null

  private isFocus = false
  private nativeFocus = false

  private ignoreComposition = false // 有 bug 版本搜狗拼音

  constructor(private domAdapter: DomAdapter,
              private parser: Parser,
              private keyboard: Keyboard,
              private commander: Commander,
              private selection: Selection,
              private controller: Controller,
              private scheduler: Scheduler,
              private textbus: Textbus) {
    super()
    this.caret = new ExperimentalCaret(this.domAdapter, this.scheduler, this.textbus.get(VIEW_MASK))
    this.onReady = new Promise<void>(resolve => {
      this.subscription.add(
        fromEvent(this.container, 'load').subscribe(() => {
          const doc = this.container.contentDocument!
          doc.open()
          doc.write(iframeHTML)
          doc.close()
          this.doc = doc
          this.init()
          resolve()
        }),
        controller.onReadonlyStateChange.subscribe(() => {
          if (controller.readonly) {
            this.blur()
          }
        })
      )
    })

    this.caret.elementRef.prepend(this.container)
  }

  focus(range: Range, restart: boolean) {
    if (!this.disabled) {
      this.caret.show(range, restart)
    }
    if (this.controller.readonly) {
      return
    }
    if (!this.isFocus) {
      this.textarea?.focus()
      setTimeout(() => {
        if (!this.nativeFocus && this.isFocus) {
          this.reInit()
        }
      })
    }
    this.isFocus = true
  }

  blur() {
    this.caret.hide()
    this.textarea?.blur()
    this.isFocus = false
  }

  destroy() {
    this.caret.destroy()
    this.subscription.unsubscribe()
  }

  private reInit(delay = false) {
    this.subscription.unsubscribe()
    this.textarea?.parentNode?.removeChild(this.textarea)
    this.subscription = new Subscription()
    this.init()
    if (delay) {
      setTimeout(() => {
        this.textarea?.focus()
      })
    } else {
      this.textarea?.focus()
    }
  }

  private init() {
    const doc = this.doc
    const contentBody = doc.body
    const textarea = doc.createElement('textarea')
    textarea.disabled = this.disabled
    contentBody.appendChild(textarea)
    this.textarea = textarea
    this.subscription.add(
      fromEvent(textarea, 'blur').subscribe(() => {
        // if (this.isFocus) {
        //   this.isFocus = false
        //   this.reInit(true)
        // }
        this.isFocus = false
        this.nativeFocus = false
        this.caret.hide()
        if (this.domAdapter.composition) {
          const slot = this.domAdapter.composition.slot
          this.domAdapter.composition = null
          this.domAdapter.compositionNode = null
          slot.__changeMarker__.forceMarkDirtied()
        }
      }),
      fromEvent(textarea, 'focus').subscribe(() => {
        this.nativeFocus = true
      }),
      this.caret.onStyleChange.subscribe(style => {
        Object.assign(textarea.style, style)
      })
    )
    this.handleInput(textarea)
    this.handleShortcut(textarea)
    this.handleDefaultActions(textarea)
  }

  private handleDefaultActions(textarea: HTMLTextAreaElement) {
    this.subscription.add(
      fromEvent<ClipboardEvent>(isFirefox() ? textarea : document, 'copy').subscribe(ev => {
        this.copyHandler(ev)
      }),
      fromEvent<ClipboardEvent>(textarea, 'paste').subscribe(ev => {
        this.pasteHandler(ev)
      })
    )
  }

  copyHandler(ev: ClipboardEvent) {
    const selection = this.selection
    if (!selection.isSelected) {
      return
    }
    if (selection.startSlot === selection.endSlot && selection.endOffset! - selection.startOffset! === 1) {
      const content = selection.startSlot!.getContentAtIndex(selection.startOffset!)
      if (typeof content === 'object') {
        const clipboardData = ev.clipboardData!
        const nativeSelection = document.getSelection()!
        const range = nativeSelection.getRangeAt(0)
        const div = document.createElement('div')
        const fragment = range.cloneContents()
        div.append(fragment)
        clipboardData.setData('text/html', div.innerHTML)
        clipboardData.setData('text', div.innerText)
        ev.preventDefault()
      }
    }
  }

  pasteHandler(ev: ClipboardEvent) {
    const text = ev.clipboardData!.getData('Text')

    const types = Array.from(ev.clipboardData!.types || [])
    const files = Array.from(ev.clipboardData!.files)
    if (types.every(type => type === 'Files') && files.length) {
      Promise.all(files.filter(i => {
        return /image/i.test(i.type)
      }).map(item => {
        const reader = new FileReader()
        return new Promise(resolve => {
          reader.onload = (event) => {
            resolve(event.target!.result)
          }
          reader.readAsDataURL(item)
        })
      })).then(urls => {
        const html = urls.map(i => {
          return `<img src=${i}>`
        }).join('')
        this.paste(html, text)
      })
      ev.preventDefault()
      return
    }

    const div = this.doc.createElement('div')
    div.style.cssText = 'width:1px; height:10px; overflow: hidden; position: fixed; left: 50%; top: 50%; opacity:0'
    div.contentEditable = 'true'
    this.doc.body.appendChild(div)
    div.focus()
    setTimeout(() => {
      this.doc.body.removeChild(div)
      div.style.cssText = ''
      this.paste(div, text)
    })
  }

  private paste(dom: HTMLElement | string, text: string) {
    const slot = this.parser.parse(dom, new Slot([
      ContentType.BlockComponent,
      ContentType.InlineComponent,
      ContentType.Text
    ]))

    this.commander.paste(slot, text)
  }

  private handleShortcut(textarea: HTMLTextAreaElement) {
    let isWriting = false
    let isIgnore = false
    this.subscription.add(
      fromEvent(textarea, 'compositionstart').subscribe(() => {
        isWriting = true
      }),
      fromEvent(textarea, 'compositionend').subscribe(() => {
        isWriting = false
      }),
      fromEvent<InputEvent>(textarea, 'beforeinput').subscribe(ev => {
        this.ignoreComposition = false
        if (this.isSafari) {
          if (ev.inputType === 'insertFromComposition') {
            isIgnore = true
          }
        }
      }),
      fromEvent<KeyboardEvent>(textarea, 'keydown').pipe(filter(() => {
        if (this.isSafari && isIgnore) {
          isIgnore = false
          return false
        }
        return !isWriting // || !this.textarea.value
      })).subscribe(ev => {
        this.ignoreComposition = false
        let key = ev.key
        const keys = ')!@#$%^Z&*('
        const b = key === 'Process' && /Digit\d/.test(ev.code) && ev.shiftKey
        if (b) {
          // 大小写锁定为大写 + 全角 + shift + 数字键，还存在问题
          key = keys.charAt(+ev.code.substring(5))
          ev.preventDefault()
        }
        this.caret.changeFromSelf = true
        const is = this.keyboard.execShortcut({
          key: key,
          altKey: ev.altKey,
          shiftKey: ev.shiftKey,
          modKey: this.isMac ? ev.metaKey : ev.ctrlKey,
          agent: {
            key: ev.key,
            code: ev.code,
            keyCode: ev.keyCode,
          }
        })
        if (is) {
          this.ignoreComposition = true
          ev.preventDefault()
        } else {
          this.caret.changeFromSelf = false
        }
      })
    )
  }

  private handleInput(textarea: HTMLTextAreaElement) {
    let startIndex = 0
    this.subscription.add(
      fromEvent<CompositionEvent>(textarea, 'compositionstart').pipe(filter(() => {
        return !this.ignoreComposition
      })).subscribe(() => {
        if (!this.selection.isCollapsed) {
          this.caret.changeFromSelf = true
          this.commander.delete()
        }
        this.composition = true
        startIndex = this.selection.startOffset!
        const startSlot = this.selection.startSlot!
        const event = new Event<Slot, CompositionStartEventData>(startSlot, {
          index: startIndex
        })
        invokeListener(startSlot.parent!, 'onCompositionStart', event)
      }),
      fromEvent<CompositionEvent>(textarea, 'compositionupdate').pipe(filter(() => {
        return !this.ignoreComposition
      })).pipe(distinctUntilChanged((prev, next) => {
        return prev.data !== next.data
      })).subscribe(ev => {
        if (ev.data === ' ') {
          // 处理搜狗五笔不符合 composition 事件预期，会意外跳光标的问题
          return
        }
        const startSlot = this.selection.startSlot!
        this.domAdapter.composition = {
          slot: startSlot,
          text: ev.data,
          offset: ev.data.length,
          index: startIndex
        }

        this.caret.changeFromSelf = true
        this.caret.refresh()
        const event = new Event(startSlot, {
          index: startIndex,
          data: ev.data
        })

        invokeListener(startSlot.parent!, 'onCompositionUpdate', event)
        startSlot.__changeMarker__.forceMarkDirtied()
      })
    )
    let isCompositionEnd = false
    this.subscription.add(
      merge(
        fromEvent<InputEvent>(textarea, 'beforeinput').pipe(
          filter(ev => {
            ev.preventDefault()
            if (this.isFirefox && ev.inputType === 'insertFromPaste') {
              return false
            }
            if (this.isSafari) {
              isCompositionEnd = ev.inputType === 'insertFromComposition'
              return ev.inputType === 'insertText' || ev.inputType === 'insertFromComposition'
            }
            return !ev.isComposing && !!ev.data
          }),
          map(ev => {
            return ev.data as string
          })
        ),
        this.isSafari ? new Observable<string>() : fromEvent<CompositionEvent>(textarea, 'compositionend')
          .pipe(filter(() => {
            return !this.ignoreComposition
          })).pipe(
            map(ev => {
              isCompositionEnd = true
              ev.preventDefault()
              textarea.value = ''
              return ev.data
            })
          )
      ).subscribe(text => {
        this.composition = false
        this.domAdapter.composition = null
        if (text) {
          this.caret.changeFromSelf = true
          this.commander.write(text)
        } else {
          this.selection.startSlot?.__changeMarker__.forceMarkDirtied()
        }
        if (isCompositionEnd) {
          const startSlot = this.selection.startSlot
          if (startSlot) {
            const event = new Event<Slot>(startSlot, null)
            invokeListener(startSlot.parent!, 'onCompositionEnd', event)
          }
        }
        isCompositionEnd = false
      })
    )
  }

  private createEditableFrame() {
    return createElement('iframe', {
      attrs: {
        scrolling: 'no'
      },
      styles: {
        border: 'none',
        width: '100%',
        display: 'block',
        height: '100%',
        position: 'absolute',
        left: 0,
        bottom: this.isWindows ? '3px' : '0'
      }
    }) as HTMLIFrameElement
  }
}
